import {
  isInvalidEmail,
  isInvalidOTP,
  isInvalidPassword,
  isInvalidUserName,
  isInvalidVendorName,
  isInvalidString
} from "src/validation/stringValidation";
import { FormValidator } from "./formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { AuthFormDataKey } from "src/types";

class AuthFormValidator extends FormValidator {
  private static validateEmail(value: unknown): ValidationError<"email"> | undefined {
    return isInvalidEmail(value) ? new ValidationError("Enter a valid email", "email") : undefined;
  }

  private static validatePassword(value: unknown): ValidationError<"password"> | undefined {
    return isInvalidPassword(value) ? new ValidationError("Invalid password", "password") : undefined;
  }

  private static validateNewPassword(value: unknown): ValidationError<"newPassword"> | undefined {
    return isInvalidPassword(value)
      ? new ValidationError("Must be at least 8 characters; 1 uppercase, 1 symbol & 1 number", "newPassword")
      : undefined;
  }

  private static validateConfirmPassword(value: unknown): ValidationError<"confirmPassword"> | undefined {
    return isInvalidPassword(value)
      ? new ValidationError("Must be at least 8 characters; 1 uppercase, 1 symbol & 1 number", "confirmPassword")
      : undefined;
  }

  private static validateOTP(value: unknown): ValidationError<"otp"> | undefined {
    return isInvalidOTP(value) ? new ValidationError("Enter a valid code", "otp") : undefined;
  }

  private static validateName(value: unknown): ValidationError<"name"> | undefined {
    return isInvalidUserName(value) ? new ValidationError("Name must be 3-30 characters", "name") : undefined;
  }

  private static validateVendorName(value: unknown): ValidationError<"vendorName"> | undefined {
    return isInvalidVendorName(value)
      ? new ValidationError("Vendor name must be 2-55 characters", "vendorName")
      : undefined;
  }

  private static validateReasonForAccountCreation(
    value: unknown
  ): ValidationError<"reasonForAccountCreation"> | undefined {
    return isInvalidString(value, 10, 500)
      ? new ValidationError("Reason must be 10-500 characters", "reasonForAccountCreation")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    AuthFormDataKey,
    (value: unknown) => ValidationError<AuthFormDataKey> | undefined
  > = {
    email: AuthFormValidator.validateEmail,
    password: AuthFormValidator.validatePassword,
    otp: AuthFormValidator.validateOTP,
    confirmPassword: AuthFormValidator.validateConfirmPassword,
    newPassword: AuthFormValidator.validateNewPassword,
    name: AuthFormValidator.validateName,
    vendorName: AuthFormValidator.validateVendorName,
    reasonForAccountCreation: AuthFormValidator.validateReasonForAccountCreation
  };

  static validateAuthFormData<T extends AuthFormDataKey>(formData: Partial<Record<T, unknown>>): ValidationErrors<T> {
    return super.validateFormData(formData, AuthFormValidator.validationStrategies) as ValidationErrors<T>;
  }
}

export { AuthFormValidator };
